/**
 * For working with Google directions.
 *
 * Utility functions for deserializing directions, such as those returned from server on Tour, or
 * serializing directions from the front end, such as those for a user plan.
 */
import { Direction } from "@dto/Direction";

const deserializeDirectionsData = (directionsData: Direction) => {
  const result = JSON.parse(JSON.stringify(directionsData));
  result.routes[0].bounds = new google.maps.LatLngBounds(
    {
      lat: result.routes[0].bounds.south,
      lng: result.routes[0].bounds.west,
    },
    {
      lat: result.routes[0].bounds.north,
      lng: result.routes[0].bounds.east,
    },
  );
  result.routes[0].overview_path = google.maps.geometry.encoding.decodePath(
    result.routes[0].overview_polyline,
  );

  for (let i = 0; i < result.routes[0].legs.length; ++i) {
    const leg = result.routes[0].legs[i];
    for (let j = 0; j < leg.steps.length; ++j) {
      const step = leg.steps[j];
      step.path = google.maps.geometry.encoding.decodePath(
        step.polyline.points,
      );
    }
  }

  return result;
};

const serializeDirectionsData = (
  directions: google.maps.DirectionsResult,
): Direction => {
  const result = {
    request: {
      travelMode: directions.request.travelMode as "DRIVING" | "WALKING",
      provideRouteAlternatives: directions.request.provideRouteAlternatives,
    },
    routes: directions.routes.map((route) => ({
      bounds: {
        south: route.bounds.getSouthWest().lat(),
        west: route.bounds.getSouthWest().lng(),
        north: route.bounds.getNorthEast().lat(),
        east: route.bounds.getNorthEast().lng(),
      },
      overview_polyline: google.maps.geometry.encoding.encodePath(
        route.overview_path,
      ),
      legs: route.legs.map((leg) => ({
        steps: leg.steps.map((step) => ({
          html_instructions: step.instructions,
          polyline: {
            points: google.maps.geometry.encoding.encodePath(step.path),
          },
          distance: {
            text: step.distance?.text,
            value: step.distance?.value,
          },
          duration: {
            text: step.duration?.text,
            value: step.duration?.value,
          },
          travel_mode: step.travel_mode as "DRIVING" | "WALKING",
        })),
        start_location: leg.start_location.toJSON(),
        end_location: leg.end_location.toJSON(),
        distance: {
          text: leg.distance?.text,
          value: leg.distance?.value,
        },
        duration: {
          text: leg.duration?.text,
          value: leg.duration?.value,
        },
      })),
    })),
  } as Direction;
  return result;
};

export { serializeDirectionsData, deserializeDirectionsData };
